import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn, getEmbedUrl } from '../lib/utils';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';

const REELS_FALLBACK = [
  'DZN3-O2juT5',
  'DZK4YvVFJqR',
  'DZF59w3jkuy',
  'DY5Wbx9gHC6',
  'DY3FdWOkqml',
  'DY0hpkDkTGm'
];

export default function VideoCarousel() {
  const [embedUrls, setEmbedUrls] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const videoDoc = await getDoc(doc(db, 'settings', 'video_carousel'));
        if (videoDoc.exists()) {
          const data = videoDoc.data();
          if (data && Array.isArray(data.links)) {
            const validLinks = data.links
              .map((link: string) => (link || '').trim())
              .filter(Boolean)
              .map((link: string) => getEmbedUrl(link))
              .filter(Boolean) as string[];

            if (validLinks.length > 0) {
              setEmbedUrls(validLinks);
              setCurrentIndex(Math.floor(validLinks.length / 2));
              setLoading(false);
              return;
            }
          }
        }
      } catch (err) {
        console.error("Error fetching video carousel settings:", err);
      }

      // Fallback
      const defaultEmbeds = REELS_FALLBACK.map(reelId => `https://www.instagram.com/reel/${reelId}/embed/`);
      setEmbedUrls(defaultEmbeds);
      setCurrentIndex(Math.floor(defaultEmbeds.length / 2));
      setLoading(false);
    };

    fetchVideos();
  }, []);

  const handleNext = useCallback(() => {
    if (embedUrls.length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex + 1) % embedUrls.length);
  }, [embedUrls]);

  const handlePrev = () => {
    if (embedUrls.length === 0) return;
    setCurrentIndex((prevIndex) => (prevIndex - 1 + embedUrls.length) % embedUrls.length);
  };
    

  if (loading || embedUrls.length === 0) {
    return (
      <section className="py-24 px-6 md:px-12 bg-zinc-50 dark:bg-zinc-950 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-zinc-500 font-medium">Cargando carrusel de videos...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 px-6 md:px-12 bg-zinc-50 dark:bg-zinc-950">
        <div className="max-w-6xl mx-auto flex flex-col items-center text-center">
          
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-zinc-900 dark:text-white mb-20 md:mb-24 relative z-10">
            Nuestro Contenido en Redes Sociales
          </h2>

          {/* Main Showcase Section */}
          <div className="relative w-full h-[450px] md:h-[550px] flex items-center justify-center [perspective:1000px]">
            {embedUrls.map((embedUrl, index) => {
              const offset = index - currentIndex;
              const total = embedUrls.length;
              let pos = (offset + total) % total;
              if (pos > Math.floor(total / 2)) {
                pos = pos - total;
              }

              const isCenter = pos === 0;
              const isAdjacent = Math.abs(pos) === 1;

              if (isMobile) {
                if (!isCenter) return null;
              } else {
                if (!isCenter && !isAdjacent) return null;
              }

              return (
                <motion.div
                  key={embedUrl + index}
                  className={cn(
                    isMobile ? 'w-full max-w-sm aspect-[9/16]' : 'absolute w-64 h-[450px] md:w-80 md:h-[550px]',
                    'flex items-center justify-center'
                  )}
                  animate={isMobile ? { opacity: 1 } : {
                    x: `${pos * 50}%`,
                    scale: isCenter ? 1 : isAdjacent ? 0.8 : 0.6,
                    rotateY: `${pos * -15}deg`,
                    zIndex: isCenter ? 10 : isAdjacent ? 5 : 1,
                    opacity: isCenter ? 1 : isAdjacent ? 0.6 : 0,
                  }}
                  transition={{ duration: isMobile ? 0 : 0.5, ease: "easeInOut" }}
                  style={{
                    filter: isCenter ? 'blur(0px)' : 'blur(4px)',
                  }}
                >
                  <iframe
                    src={embedUrl}
                    className="object-cover w-full h-full rounded-3xl border-2 border-zinc-200 dark:border-zinc-800 shadow-2xl bg-zinc-900"
                    frameBorder="0"
                    scrolling="no"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={`Video ${index + 1}`}
                  />
                </motion.div>
              );
            })}
            
            {/* Navigation Buttons */}
            {embedUrls.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Video anterior"
                  className="absolute left-2 sm:left-8 top-1/2 -translate-y-1/2 rounded-full h-12 w-12 z-20 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm flex items-center justify-center hover:bg-primary hover:text-white transition-colors text-zinc-900 dark:text-white"
                  onClick={handlePrev}
                >
                  <ChevronLeft className="h-6 w-6" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label="Video siguiente"
                  className="absolute right-2 sm:right-8 top-1/2 -translate-y-1/2 rounded-full h-12 w-12 z-20 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm flex items-center justify-center hover:bg-primary hover:text-white transition-colors text-zinc-900 dark:text-white"
                  onClick={handleNext}
                >
                  <ChevronRight className="h-6 w-6" aria-hidden="true" />
                </button>
              </>
            )}
          </div>
        </div>
    </section>
  );
}
